// web/src/components/charts/CitiesPieChart.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";

const COLORS = ["#ff4f9a", "#ff87c8", "#ffa6d8", "#ffd2e8", "#ffe6f2"];

function normalizeCity(raw) {
  if (!raw) return "";

  // 1) trim + remove espaços duplicados
  let s = String(raw).trim().replace(/\s+/g, " ");
  if (!s) return "";

  // 2) lowercase
  s = s.toLowerCase();

  // 3) capitalização simples por palavra (mantém "do/da/de" em minúsculo)
  const keepLower = new Set(["de", "da", "do", "das", "dos", "e"]);
  s = s
    .split(" ")
    .map((w) => {
      if (!w) return w;
      if (keepLower.has(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");

  return s;
}

function CitiesPieChart() {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ para rodapé com totais reais
  const [totals, setTotals] = useState({
    totalUsers: 0,
    withCity: 0,
    withoutCity: 0,
  });

  useEffect(() => {
    // ✅ trocado para onSnapshot (atualiza sozinho)
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const cityCount = {};
        let totalUsers = snap.size;
        let withCity = 0;
        let withoutCity = 0;

        snap.forEach((doc) => {
          const rawCity = doc.data()?.city;

          const city = normalizeCity(rawCity);

          if (!city) {
            withoutCity++;
            return;
          }

          withCity++;
          cityCount[city] = (cityCount[city] || 0) + 1;
        });

        // Ordena por maior volume
        const entries = Object.entries(cityCount).sort((a, b) => b[1] - a[1]);

        // Top 4 + Outros
        const topLimit = 4;
        const topCities = [];
        let outrosTotal = 0;

        entries.forEach(([city, value], index) => {
          if (index < topLimit) topCities.push({ name: city, value });
          else outrosTotal += value;
        });

        if (outrosTotal > 0) {
          topCities.push({ name: "Outros", value: outrosTotal });
        }

        // ✅ Se quiser exibir "Sem cidade" no gráfico (eu recomendo):
        // ajuda a enxergar se cadastro está incompleto.
        if (withoutCity > 0) {
          topCities.push({ name: "Sem cidade", value: withoutCity });
        }

        setChartData(topCities);
        setTotals({ totalUsers, withCity, withoutCity });
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar cidades:", err);
        setChartData([]);
        setTotals({ totalUsers: 0, withCity: 0, withoutCity: 0 });
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const totalChart = useMemo(() => {
    return chartData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [chartData]);

  // ESTILOS INLINE TOTAL - NENHUM CSS EXTERNO
  const styles = {
    container: {
      background: "#FFFFFF",
      borderRadius: "16px",
      padding: "24px",
      boxShadow: "0 8px 25px rgba(255, 79, 154, 0.15)",
      minHeight: "420px",
      border: "1px solid rgba(255, 79, 154, 0.08)",
      display: "flex",
      flexDirection: "column",
      width: "100%",
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
      margin: 0,
      isolation: "isolate",
    },
    title: {
      margin: "0 0 20px 0",
      color: "#ff4f9a",
      fontWeight: "700",
      fontSize: "18px",
      paddingBottom: "15px",
      borderBottom: "1px solid rgba(255, 79, 154, 0.1)",
      flexShrink: 0,
    },
    chartWrapper: {
      width: "100%",
      height: "320px",
      flex: 1,
      position: "relative",
      backgroundColor: "transparent",
    },
    footer: {
      marginTop: "15px",
      paddingTop: "10px",
      borderTop: "1px solid #eee",
      fontSize: "12px",
      color: "#666",
      textAlign: "center",
      flexShrink: 0,
      lineHeight: 1.4,
    },
    loading: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#888",
      fontSize: "14px",
    },
    empty: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#999",
      fontSize: "13px",
      textAlign: "center",
      padding: "24px",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Cidades dos usuários 📍</h3>
        <div style={styles.loading}>Carregando gráfico...</div>
      </div>
    );
  }

  // Se não houver dados (ex.: nenhum user)
  if (!chartData || chartData.length === 0) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Cidades dos usuários 📍</h3>
        <div style={styles.empty}>
          Sem dados suficientes para exibir o gráfico.
          <br />
          (Nenhum usuário encontrado ou nenhum registro válido.)
        </div>
        <div style={styles.footer}>
          Total: <strong>{totals.totalUsers}</strong> usuários
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Cidades dos usuários 📍</h3>

      <div style={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={90}
              innerRadius={40}
              paddingAngle={2}
              // ✅ label com valor (quantidade)
              label={({ name, value }) => `${name}: ${value}`}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              ))}
            </Pie>

            <Tooltip
              formatter={(value) => [`${value} usuários`, "Quantidade"]}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ff4f9a",
                borderRadius: "8px",
                fontSize: "12px",
                padding: "8px",
              }}
            />

            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{
                fontSize: "12px",
                paddingTop: "5px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div style={styles.footer}>
        <div>
          Total usuários: <strong>{totals.totalUsers}</strong>
        </div>
        <div>
          Com cidade: <strong>{totals.withCity}</strong> • Sem cidade:{" "}
          <strong>{totals.withoutCity}</strong>
        </div>
        <div style={{ opacity: 0.8 }}>
          Total no gráfico: <strong>{totalChart}</strong>
        </div>
      </div>
    </div>
  );
}

export default CitiesPieChart;
